// In-depth Node.js lessons (Codeazy lesson standard).

export const nodeLessons = [
  {
    slug: "what-is-node",
    title: "How Node.js Works",
    summary: "The runtime, the event loop and libuv, modules, npm, and when Node is (and isn’t) the right choice.",
    readTime: 13,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Node.js powers the backends of Netflix, PayPal, LinkedIn and countless startups, and it is the “N” in MERN. Knowing how it handles thousands of connections on a single thread explains its strengths, its weaknesses and many interview questions." },

      { type: "h2", text: "The mental model: a waiter, not a cook" },
      { type: "p", text: "A traditional thread-per-request server is like a restaurant where each customer gets a **dedicated waiter** who stands at the kitchen door waiting for their food. Lots of waiters stand around idle. Node is **one very fast waiter**: they take an order, hand it to the kitchen (the OS or a thread pool) and immediately serve the next table. When food is ready, the kitchen rings a bell (an event) and the waiter delivers it." },
      { type: "callout", variant: "note", text: "That is why Node excels at I/O-heavy work such as APIs, chat, streaming and proxies, which spends most of its time waiting on databases and networks, and struggles with CPU-heavy work that keeps the single waiter busy." },

      { type: "h2", text: "What Node is made of" },
      { type: "table", headers: ["Part", "Role"], rows: [
        ["**V8**", "Google’s JavaScript engine (also used in Chrome). It compiles JavaScript to machine code."],
        ["**libuv**", "A C library that provides the event loop, async file system, networking, timers and a thread pool."],
        ["**Thread pool**", "4 threads by default (`UV_THREADPOOL_SIZE`) for operations without async OS APIs: file system, DNS lookups, crypto, compression."],
        ["**Node APIs**", "`fs`, `http`, `path`, `crypto`, `events`, `stream` and more, written in JavaScript and C++."]
      ] },

      { type: "h2", text: "Blocking vs non-blocking" },
      { type: "compare", badLabel: "Blocking (sync)", bad: `const fs = require("fs");

const data = fs.readFileSync("big.csv", "utf8");
// the whole server waits here, and
// no other request is handled
console.log(data.length);`, goodLabel: "Non-blocking (async)", good: `const fs = require("fs/promises");

async function load() {
  const data = await fs.readFile("big.csv", "utf8");
  // other requests are served while reading
  console.log(data.length);
}` },
      { type: "callout", variant: "tip", text: "`*Sync` functions are fine in startup scripts and CLI tools, but never use them inside request handlers." },

      { type: "h2", text: "The Node event loop phases" },
      { type: "p", text: "Node’s event loop goes through phases in order. In each one it runs the callbacks queued for that phase:" },
      { type: "table", headers: ["Phase", "Runs"], rows: [
        ["1. Timers", "`setTimeout` and `setInterval` callbacks whose time has come"],
        ["2. Pending callbacks", "Some deferred system I/O callbacks"],
        ["3. Poll", "Waits for and runs I/O callbacks (network, files)"],
        ["4. Check", "`setImmediate` callbacks"],
        ["5. Close callbacks", "Such as `socket.on(\"close\")`"]
      ] },
      { type: "p", text: "Between every callback, Node drains two high-priority queues: first `process.nextTick`, then promise microtasks." },
      { type: "code", language: "javascript", code: `const fs = require("fs");

fs.readFile(__filename, () => {
  setTimeout(() => console.log("timeout"), 0);
  setImmediate(() => console.log("immediate"));
  process.nextTick(() => console.log("nextTick"));
  Promise.resolve().then(() => console.log("promise"));
});`, output: `nextTick
promise
immediate
timeout` },
      { type: "p", text: "Inside an I/O callback, we are in the poll phase. `nextTick` and promises run immediately after the current callback, then the loop moves on to the **check** phase (`setImmediate`) before going round to **timers** again." },

      { type: "h2", text: "Modules: CommonJS vs ES modules" },
      { type: "compare", badLabel: "CommonJS (classic Node)", bad: `// math.js
function add(a, b) { return a + b; }
module.exports = { add };

// app.js
const { add } = require("./math");`, goodLabel: "ES Modules (modern standard)", good: `// math.js
export function add(a, b) { return a + b; }

// app.js
import { add } from "./math.js";

// package.json: { "type": "module" }` },
      { type: "table", headers: ["", "CommonJS", "ES Modules"], rows: [
        ["Syntax", "`require` / `module.exports`", "`import` / `export`"],
        ["Loading", "Synchronous, at runtime", "Asynchronous, analysed statically"],
        ["Top-level `await`", "No", "Yes"],
        ["`__dirname`", "Available", "Use `import.meta.dirname` (Node 20.11+)"],
        ["Enable", "Default for `.js` and `.cjs`", "`\"type\": \"module\"` or `.mjs`"]
      ] },

      { type: "h2", text: "npm and package.json" },
      { type: "code", language: "json", code: `{
  "name": "codeazy-api",
  "version": "1.0.0",
  "type": "module",
  "scripts": {
    "dev": "node --watch src/server.js",
    "start": "node src/server.js",
    "test": "node --test"
  },
  "dependencies": {
    "express": "^4.19.2",
    "mongoose": "^8.4.0"
  },
  "devDependencies": {
    "eslint": "^9.0.0"
  }
}` },
      { type: "table", headers: ["Version range", "Allows", "Example for 4.19.2"], rows: [
        ["`^4.19.2` (caret, the default)", "Minor and patch updates", "4.20.0 ✓, 5.0.0 ✗"],
        ["`~4.19.2` (tilde)", "Patch updates only", "4.19.9 ✓, 4.20.0 ✗"],
        ["`4.19.2` (exact)", "Only that version", "-"]
      ] },
      { type: "ul", items: [
        "**`package-lock.json`** records the exact versions installed. Commit it so every machine installs the same tree.",
        "**`npm ci`** installs strictly from the lockfile, which is ideal for CI and production.",
        "**`dependencies`** are needed at runtime. **`devDependencies`** are only for building, testing and linting.",
        "**`npx`** runs a package’s binary without installing it globally."
      ] },

      { type: "h2", text: "EventEmitter and streams" },
      { type: "code", language: "javascript", code: `import { EventEmitter } from "node:events";

const orders = new EventEmitter();
orders.on("placed", (order) => console.log("Email receipt for", order.id));
orders.on("placed", (order) => console.log("Notify warehouse:", order.id));

orders.emit("placed", { id: 42 });`, output: `Email receipt for 42
Notify warehouse: 42` },
      { type: "p", text: "**Streams** process data piece by piece instead of loading everything into memory. They are essential for large files, uploads and video:" },
      { type: "code", language: "javascript", code: `import { createReadStream, createWriteStream } from "node:fs";
import { createGzip } from "node:zlib";
import { pipeline } from "node:stream/promises";

// Compress a 5 GB log file using only a few MB of memory
await pipeline(
  createReadStream("app.log"),
  createGzip(),
  createWriteStream("app.log.gz")
);` },

      { type: "h2", text: "When (not) to use Node" },
      { type: "table", headers: ["Great fit", "Poor fit (or needs workers)"], rows: [
        ["REST and GraphQL APIs", "Video encoding or image processing on the main thread"],
        ["Real-time apps: chat, notifications, live dashboards", "Heavy number crunching and ML training"],
        ["BFFs and API gateways", "Long CPU-bound loops in request handlers"],
        ["Streaming and proxies", ""],
        ["Tooling and CLIs (Vite, ESLint, npm itself)", ""]
      ] },
      { type: "p", text: "For CPU-heavy work, use **worker threads** (`node:worker_threads`), a job queue with separate worker processes, or a service in another language." },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "Node.js is single-threaded. How does it handle many concurrent requests?", answer: ["JavaScript runs on one thread, but I/O is delegated to the operating system’s async APIs or libuv’s thread pool. While waiting, the event loop keeps processing other requests. When an operation completes, its callback is queued and run. This lets one process handle thousands of concurrent I/O-bound connections."] },
      { type: "qa", question: "What is the difference between `process.nextTick` and `setImmediate`?", answer: ["`process.nextTick` callbacks run immediately after the current operation, before promise microtasks and before the event loop continues. `setImmediate` runs in the check phase of the next loop iteration, after I/O polling. Overusing `nextTick` can starve the event loop."] },
      { type: "qa", question: "What is libuv?", answer: ["The C library underneath Node that implements the event loop, the thread pool and cross-platform async I/O for files, networking, DNS and timers."] },
      { type: "qa", question: "What is the difference between `dependencies` and `devDependencies`?", answer: ["`dependencies` are required at runtime in production, such as Express or Mongoose. `devDependencies` are only needed during development and builds, such as test runners, linters and bundlers. `npm ci --omit=dev` skips them in production images."] },
      { type: "qa", question: "Why are streams useful?", answer: ["They process data in chunks, so memory use stays low and constant regardless of file size, and processing can start before all the data arrives. `pipeline()` also handles backpressure and errors between stages."] },
      { type: "qa", question: "How would you run CPU-intensive work in Node?", answer: ["Move it off the main thread: worker threads for in-process parallelism, child processes, or a job queue (such as BullMQ with Redis) processed by separate worker services. Keep request handlers fast and non-blocking."] },

      { type: "exercise", title: "Build a tiny HTTP server without Express", task: "Using only `node:http`, create a server on port 3000 that responds to `GET /api/health` with JSON `{ \"status\": \"ok\" }`, to `GET /api/time` with the current ISO time, and with a 404 JSON error for anything else.", language: "javascript", solution: `import { createServer } from "node:http";

function sendJson(res, status, body) {
  res.writeHead(status, { "Content-Type": "application/json" });
  res.end(JSON.stringify(body));
}

const server = createServer((req, res) => {
  if (req.method === "GET" && req.url === "/api/health") {
    return sendJson(res, 200, { status: "ok" });
  }
  if (req.method === "GET" && req.url === "/api/time") {
    return sendJson(res, 200, { now: new Date().toISOString() });
  }
  sendJson(res, 404, { error: "Not found" });
});

server.listen(3000, () => console.log("Listening on http://localhost:3000"));`, explanation: "This is what Express does for you under the hood: matching the method and URL, setting headers and serialising JSON. Writing it once by hand makes Express’s routing and middleware much easier to understand." },

      { type: "summary", items: [
        "Node is V8 plus libuv: one JavaScript thread, with async I/O handled by the OS and a thread pool.",
        "It shines for I/O-heavy servers. Move CPU-heavy work to workers or queues.",
        "Never use `*Sync` APIs in request handlers.",
        "The event loop runs in phases, with `nextTick` and promises drained between callbacks.",
        "Use ES modules, commit your lockfile, and use streams for large data."
      ] }
    ]
  },

  {
    slug: "express-rest-api",
    title: "Building REST APIs with Express",
    summary: "Design clean REST endpoints, structure an Express app, and handle params, queries, bodies and status codes.",
    readTime: 14,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Almost every full-stack role involves building or consuming REST APIs. A well-designed API is predictable, easy for frontend developers to use and easy to extend. Express is the most widely used Node framework, and its patterns appear in most others." },

      { type: "h2", text: "The mental model: a restaurant menu" },
      { type: "p", text: "A REST API is a **menu** of resources (nouns) and what you can do with them (HTTP methods as verbs). `/courses` is a dish category. `GET` means show me, `POST` means order a new one, `PATCH` means change part of my order, and `DELETE` means cancel it. The URLs stay noun-based, and the method says what action to take." },

      { type: "h2", text: "REST design cheat sheet" },
      { type: "table", headers: ["Method + path", "Action", "Success code"], rows: [
        ["`GET /api/courses`", "List courses (with filters and pagination)", "200 OK"],
        ["`GET /api/courses/:id`", "Get one course", "200 OK (404 if missing)"],
        ["`POST /api/courses`", "Create a course", "201 Created"],
        ["`PATCH /api/courses/:id`", "Update some fields", "200 OK"],
        ["`PUT /api/courses/:id`", "Replace the whole course", "200 OK"],
        ["`DELETE /api/courses/:id`", "Delete a course", "204 No Content"],
        ["`GET /api/courses/:id/lessons`", "Nested resource: a course’s lessons", "200 OK"]
      ] },
      { type: "table", headers: ["Status", "Meaning", "When"], rows: [
        ["400 Bad Request", "Invalid input", "Validation failed"],
        ["401 Unauthorized", "Not authenticated", "Missing or invalid token"],
        ["403 Forbidden", "Authenticated, but not allowed", "A student trying an admin action"],
        ["404 Not Found", "The resource doesn’t exist", "Unknown ID"],
        ["409 Conflict", "Conflicts with the current state", "Email already registered"],
        ["422 Unprocessable Entity", "Well-formed but semantically invalid", "Some teams use it for validation errors"],
        ["429 Too Many Requests", "Rate limited", "Too many login attempts"],
        ["500 Internal Server Error", "Unexpected server failure", "A bug or crash"]
      ] },
      { type: "compare", badLabel: "Verbs in URLs", bad: `GET  /getAllCourses
POST /createCourse
POST /deleteCourse?id=5
GET  /course_list_by_level?lvl=2`, goodLabel: "Nouns + HTTP methods", good: `GET    /api/courses
POST   /api/courses
DELETE /api/courses/5
GET    /api/courses?level=intermediate` },

      { type: "h2", text: "A complete Express app" },
      { type: "code", language: "javascript", code: `import express from "express";

const app = express();
app.use(express.json());                 // parse JSON bodies into req.body

let courses = [
  { id: 1, title: "DSA", level: "all", price: 4999 },
  { id: 2, title: "System Design", level: "intermediate", price: 6999 }
];
let nextId = 3;

// GET /api/courses?level=intermediate&sort=price
app.get("/api/courses", (req, res) => {
  const { level, sort } = req.query;     // query strings are always strings
  let result = courses;
  if (level) result = result.filter((c) => c.level === level);
  if (sort === "price") result = result.toSorted((a, b) => a.price - b.price);
  res.json({ data: result, count: result.length });
});

// GET /api/courses/2
app.get("/api/courses/:id", (req, res) => {
  const course = courses.find((c) => c.id === Number(req.params.id));
  if (!course) return res.status(404).json({ error: "Course not found" });
  res.json({ data: course });
});

// POST /api/courses
app.post("/api/courses", (req, res) => {
  const { title, level = "beginner", price } = req.body;
  if (!title || typeof price !== "number") {
    return res.status(400).json({ error: "title and numeric price are required" });
  }
  const course = { id: nextId++, title, level, price };
  courses.push(course);
  res.status(201).location("/api/courses/" + course.id).json({ data: course });
});

// PATCH /api/courses/2
app.patch("/api/courses/:id", (req, res) => {
  const course = courses.find((c) => c.id === Number(req.params.id));
  if (!course) return res.status(404).json({ error: "Course not found" });
  const allowed = ["title", "level", "price"];          // never trust the whole body
  for (const key of allowed) {
    if (key in req.body) course[key] = req.body[key];
  }
  res.json({ data: course });
});

// DELETE /api/courses/2
app.delete("/api/courses/:id", (req, res) => {
  const before = courses.length;
  courses = courses.filter((c) => c.id !== Number(req.params.id));
  if (courses.length === before) return res.status(404).json({ error: "Course not found" });
  res.status(204).end();
});

app.listen(8000, () => console.log("API on http://localhost:8000"));` },

      { type: "h2", text: "Where request data comes from" },
      { type: "table", headers: ["Source", "Example request", "Read with"], rows: [
        ["Route params", "`/api/courses/42`", "`req.params.id` → `\"42\"` (a string!)"],
        ["Query string", "`/api/courses?level=beginner&page=2`", "`req.query.level`, `req.query.page`"],
        ["JSON body", "`POST` with `{ \"title\": \"DSA\" }`", "`req.body.title` (needs `express.json()`)"],
        ["Headers", "`Authorization: Bearer abc`", "`req.get(\"authorization\")`"],
        ["Cookies", "`Cookie: session=...`", "`req.cookies` (needs `cookie-parser`)"]
      ] },

      { type: "h2", text: "Structuring a real project" },
      { type: "code", language: "text", code: `src/
├── server.js            # starts the HTTP server
├── app.js               # creates the app, mounts middleware and routes
├── routes/
│   └── courses.routes.js    # URL → controller mapping
├── controllers/
│   └── courses.controller.js # reads req, calls a service, sends res
├── services/
│   └── courses.service.js    # business logic, no req/res here
├── models/
│   └── Course.js             # database schema
└── middleware/
    ├── auth.js
    ├── validate.js
    └── errorHandler.js` },
      { type: "code", language: "javascript", code: `// routes/courses.routes.js
import { Router } from "express";
import * as ctrl from "../controllers/courses.controller.js";

const router = Router();
router.get("/", ctrl.list);
router.get("/:id", ctrl.getById);
router.post("/", ctrl.create);
export default router;

// app.js
app.use("/api/courses", coursesRouter);` },
      { type: "callout", variant: "tip", text: "Keep `req` and `res` out of your services. A service like `createCourse(data)` can then be reused by a CLI, a queue worker or tests without faking HTTP objects." },

      { type: "h2", text: "Pagination, filtering and consistent responses" },
      { type: "code", language: "javascript", code: `// GET /api/courses?page=2&limit=20
app.get("/api/courses", async (req, res) => {
  const page = Math.max(1, Number(req.query.page) || 1);
  const limit = Math.min(100, Number(req.query.limit) || 20);   // cap the limit

  const [items, total] = await Promise.all([
    Course.find().skip((page - 1) * limit).limit(limit),
    Course.countDocuments()
  ]);

  res.json({
    data: items,
    meta: { page, limit, total, totalPages: Math.ceil(total / limit) }
  });
});` },
      { type: "p", text: "Pick one response shape, such as `{ data, meta }` for success and `{ error: { code, message } }` for failures, and use it everywhere so frontend code can handle every endpoint the same way." },

      { type: "h2", text: "Common mistakes" },
      { type: "compare", badLabel: "Mass assignment", bad: `app.patch("/api/users/:id", async (req, res) => {
  await User.updateOne({ _id: req.params.id }, req.body);
  // attacker sends { "role": "admin" }
});`, goodLabel: "Allow-listed fields", good: `app.patch("/api/users/:id", async (req, res) => {
  const { name, bio } = req.body;
  await User.updateOne({ _id: req.params.id }, { name, bio });
});` },
      { type: "compare", badLabel: "Forgetting return", bad: `if (!course) {
  res.status(404).json({ error: "Not found" });
}
res.json(course);
// Error: Cannot set headers after they are sent`, goodLabel: "Return after responding", good: `if (!course) {
  return res.status(404).json({ error: "Not found" });
}
res.json(course);` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What makes an API RESTful?", answer: ["Resources identified by URLs (nouns), standard HTTP methods for actions, stateless requests where each carries everything needed such as auth tokens, standard status codes, and representations such as JSON. Good REST APIs are also consistent, cacheable where appropriate and versioned."] },
      { type: "qa", question: "What is the difference between PUT and PATCH?", answer: ["PUT replaces the entire resource with the request body, so missing fields are removed or reset, and it is idempotent. PATCH applies a partial update to specific fields."] },
      { type: "qa", question: "What does idempotent mean? Which methods are idempotent?", answer: ["Making the same request several times has the same effect as making it once. GET, PUT, DELETE, HEAD and OPTIONS are idempotent. POST is not, because each call usually creates a new resource. This matters for safe retries."] },
      { type: "qa", question: "What is the difference between 401 and 403?", answer: ["401 Unauthorized means the client isn’t authenticated: the credentials are missing or invalid. 403 Forbidden means the client is authenticated but doesn’t have permission for this action."] },
      { type: "qa", question: "How do you version an API?", answer: ["Most commonly with a URL prefix such as `/api/v1/courses`, which is easy to see and route. Alternatives are a header like `Accept-Version`, or media types. Version only for breaking changes, and keep old versions running during migrations."] },
      { type: "qa", question: "How would you implement pagination?", answer: ["Offset pagination with `page` and `limit` (skip and limit) is simple and supports jumping to page N, but gets slow and inconsistent on large, changing datasets. Cursor pagination with `?after=<lastId>` uses an indexed field, stays fast and is stable under inserts, which suits infinite scroll."] },

      { type: "exercise", title: "Add a search endpoint", task: "Add `GET /api/courses/search?q=design&minPrice=1000&maxPrice=8000` to the in-memory API. Match `q` case-insensitively against the title, apply the optional price range, return `400` if `q` is missing, and use the `{ data, count }` response shape.", language: "javascript", solution: `app.get("/api/courses/search", (req, res) => {
  const { q, minPrice, maxPrice } = req.query;
  if (!q || !q.trim()) {
    return res.status(400).json({ error: "Query parameter q is required" });
  }

  const min = minPrice !== undefined ? Number(minPrice) : 0;
  const max = maxPrice !== undefined ? Number(maxPrice) : Infinity;
  if (Number.isNaN(min) || Number.isNaN(max)) {
    return res.status(400).json({ error: "minPrice and maxPrice must be numbers" });
  }

  const term = q.trim().toLowerCase();
  const data = courses.filter(
    (c) => c.title.toLowerCase().includes(term) && c.price >= min && c.price <= max
  );
  res.json({ data, count: data.length });
});

// Register this BEFORE app.get("/api/courses/:id"),
// otherwise "search" is treated as an :id.`, explanation: "Query values are strings, so convert and validate them. Route order matters in Express: `/api/courses/:id` would also match `/api/courses/search`, so the more specific route must come first." },

      { type: "summary", items: [
        "Design around resources (nouns) and HTTP methods (verbs), with correct status codes.",
        "Read input from `req.params`, `req.query` and `req.body`. Params and queries are strings.",
        "Structure the app as routes, controllers, services and models, and keep `req`/`res` out of services.",
        "Allow-list updatable fields, `return` after sending a response, and mind route order.",
        "Use a consistent response shape, and paginate list endpoints."
      ] }
    ]
  },

  {
    slug: "middleware-and-errors",
    title: "Middleware, Validation and Error Handling",
    summary: "The middleware pipeline, request validation with schemas, and centralised error handling that never leaks secrets.",
    readTime: 13,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Authentication, logging, rate limiting, validation and error handling are all middleware. A clean middleware pipeline is what turns a pile of route handlers into a secure, maintainable API, and a missing error handler is how servers crash or leak stack traces to attackers." },

      { type: "h2", text: "The mental model: airport security" },
      { type: "p", text: "A request passes through a series of **checkpoints** before reaching the gate (your route handler): check-in (parse the body), ID check (authentication), security scan (validation), and so on. Each checkpoint can let the passenger through (`next()`), stop them (`res.status(401)...`) or report a problem (`next(err)`), which sends them to the special handling desk (the error handler)." },
      { type: "code", language: "text", code: `request
   │
   ▼
[helmet] → [cors] → [express.json] → [requestLogger] → [rateLimit]
   │
   ▼
[authenticate] → [validate(schema)] → route handler → response
   │                                       │
   └──────────── next(err) ───────────────► [errorHandler] → error response` },

      { type: "h2", text: "Writing middleware" },
      { type: "code", language: "javascript", code: `// Signature: (req, res, next)
function requestLogger(req, res, next) {
  const start = Date.now();
  res.on("finish", () => {
    const ms = Date.now() - start;
    console.log(req.method, req.originalUrl, res.statusCode, ms + "ms");
  });
  next();   // without this, the request hangs forever
}

app.use(requestLogger);                     // every request
app.use("/api/admin", requireAdmin);        // only paths under /api/admin
app.get("/api/me", authenticate, getMe);    // only this route` },
      { type: "table", headers: ["A middleware can…", "How"], rows: [
        ["Pass control on", "`next()`"],
        ["End the request", "`res.status(403).json(...)` and don’t call `next`"],
        ["Attach data for later handlers", "`req.user = user; next();`"],
        ["Report an error", "`next(err)`, which skips straight to the error handler"]
      ] },
      { type: "callout", variant: "warning", text: "Order matters. Middleware runs in the order you register it. `express.json()` must come before routes that read `req.body`, and the error handler must be registered **last**." },

      { type: "h2", text: "Essential production middleware" },
      { type: "code", language: "javascript", code: `import helmet from "helmet";
import cors from "cors";
import rateLimit from "express-rate-limit";

app.use(helmet());                                       // secure HTTP headers
app.use(cors({ origin: ["https://codeazy.in"], credentials: true }));
app.use(express.json({ limit: "100kb" }));               // reject huge bodies
app.use("/api/auth", rateLimit({ windowMs: 15 * 60 * 1000, limit: 20 }));  // slow down brute force` },

      { type: "h2", text: "Validation with a schema" },
      { type: "p", text: "Never trust input. Validate the **shape, types and limits** of every request with a schema library such as Zod, Joi or express-validator, in one reusable middleware:" },
      { type: "code", language: "javascript", code: `import { z } from "zod";

const createCourseSchema = z.object({
  title: z.string().trim().min(3).max(120),
  level: z.enum(["beginner", "intermediate", "advanced"]),
  price: z.number().int().nonnegative(),
  tags: z.array(z.string()).max(10).default([])
});

function validate(schema) {
  return (req, res, next) => {
    const result = schema.safeParse(req.body);
    if (!result.success) {
      return res.status(400).json({
        error: {
          code: "VALIDATION_ERROR",
          details: result.error.issues.map((i) => ({
            field: i.path.join("."),
            message: i.message
          }))
        }
      });
    }
    req.body = result.data;     // cleaned and typed, unknown keys stripped
    next();
  };
}

app.post("/api/courses", authenticate, validate(createCourseSchema), createCourse);` },
      { type: "code", language: "json", code: `// POST { "title": "AI", "level": "expert", "price": -5 }
{
  "error": {
    "code": "VALIDATION_ERROR",
    "details": [
      { "field": "title", "message": "String must contain at least 3 character(s)" },
      { "field": "level", "message": "Invalid enum value..." },
      { "field": "price", "message": "Number must be greater than or equal to 0" }
    ]
  }
}` },

      { type: "h2", text: "Centralised error handling" },
      { type: "p", text: "Express recognises an error handler by its **four** parameters. Combine it with a custom error class so the code throwing an error decides the status code:" },
      { type: "code", language: "javascript", code: `// errors.js
export class AppError extends Error {
  constructor(status, code, message) {
    super(message);
    this.status = status;
    this.code = code;
  }
}
export const notFound = (what) => new AppError(404, "NOT_FOUND", what + " not found");

// in a service or controller
const course = await Course.findById(id);
if (!course) throw notFound("Course");

// middleware/errorHandler.js: registered LAST
export function errorHandler(err, req, res, next) {
  const status = err.status || 500;

  if (status >= 500) {
    console.error(err);                        // full details only in the logs
  }

  res.status(status).json({
    error: {
      code: err.code || "INTERNAL_ERROR",
      message: status >= 500 ? "Something went wrong" : err.message   // never leak internals
    }
  });
}

// 404 for unknown routes, just before the error handler
app.use((req, res) => res.status(404).json({ error: { code: "NOT_FOUND", message: "Route not found" } }));
app.use(errorHandler);` },

      { type: "h3", text: "Async errors" },
      { type: "p", text: "In **Express 4**, a rejected promise inside an `async` handler is not caught automatically. The request hangs, and the rejection is unhandled. **Express 5** forwards rejected promises to the error handler for you." },
      { type: "compare", badLabel: "Express 4: async errors escape", bad: `app.get("/api/courses/:id", async (req, res) => {
  const course = await Course.findById(req.params.id);
  // if this throws, Express 4 never sees it
  res.json(course);
});`, goodLabel: "Wrap the handler (or use Express 5)", good: `const asyncHandler = (fn) => (req, res, next) =>
  Promise.resolve(fn(req, res, next)).catch(next);

app.get("/api/courses/:id", asyncHandler(async (req, res) => {
  const course = await Course.findById(req.params.id);
  if (!course) throw notFound("Course");
  res.json({ data: course });
}));` },

      { type: "h3", text: "Last-resort process handlers" },
      { type: "code", language: "javascript", code: `process.on("unhandledRejection", (reason) => {
  console.error("Unhandled rejection:", reason);
});

process.on("uncaughtException", (err) => {
  console.error("Uncaught exception:", err);
  process.exit(1);          // state may be corrupt, so let the process manager restart
});` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is middleware in Express?", answer: ["A function with the signature `(req, res, next)` that runs during the request–response cycle. It can read or modify `req` and `res`, end the response, or call `next()` to pass control on. Middleware is used for parsing, logging, auth, validation, rate limiting and error handling, and runs in registration order."] },
      { type: "qa", question: "How does Express identify an error-handling middleware?", answer: ["By its arity: it has four parameters, `(err, req, res, next)`. Calling `next(err)` anywhere skips the remaining normal middleware and goes to the next error handler. Register error handlers after all routes."] },
      { type: "qa", question: "How do you handle errors in async route handlers?", answer: ["In Express 4, wrap async handlers in a helper that catches rejections and calls `next(err)`, or use the `express-async-errors` package. Express 5 does this natively. Use a central error handler to format responses."] },
      { type: "qa", question: "Why shouldn’t you send `err.message` or stack traces to clients for 500 errors?", answer: ["They can reveal internal details such as file paths, SQL or queries, library versions and secrets, which help attackers. Log full details on the server, and return a generic message with an error code or request ID the user can quote to support."] },
      { type: "qa", question: "Why validate requests on the server if the frontend already validates?", answer: ["Anyone can send requests directly with curl or Postman, bypassing the frontend. Server-side validation protects data integrity and security, prevents crashes from unexpected types, and stops injection and mass-assignment attacks."] },

      { type: "exercise", title: "Write a role-based authorisation middleware", task: "Write `requireRole(...roles)`, which returns middleware that responds `401` if `req.user` is missing, `403` if the user’s role isn’t in `roles`, and otherwise calls `next()`. Show how to protect `DELETE /api/courses/:id` for admins only.", language: "javascript", solution: `function requireRole(...roles) {
  return (req, res, next) => {
    if (!req.user) {
      return res.status(401).json({ error: { code: "UNAUTHENTICATED", message: "Please log in" } });
    }
    if (!roles.includes(req.user.role)) {
      return res.status(403).json({ error: { code: "FORBIDDEN", message: "You don't have permission" } });
    }
    next();
  };
}

app.delete(
  "/api/courses/:id",
  authenticate,              // sets req.user from the token
  requireRole("admin"),      // then checks permission
  asyncHandler(deleteCourse)
);`, explanation: "This is a middleware factory: a function that returns middleware configured with the allowed roles. Chaining `authenticate` and then `requireRole` separates “who are you?” (401) from “are you allowed?” (403)." },

      { type: "summary", items: [
        "Middleware are checkpoints with the signature `(req, res, next)`, run in registration order.",
        "Use helmet, CORS, body size limits and rate limiting in production.",
        "Validate every request with a schema, and replace `req.body` with the cleaned data.",
        "Centralise errors in a four-argument handler registered last, and never leak internals on 500s.",
        "Catch async errors with a wrapper in Express 4, or use Express 5."
      ] }
    ]
  },

  {
    slug: "authentication-jwt",
    title: "Authentication with JWT and Sessions",
    summary: "Hash passwords properly, issue and verify JWTs, store tokens safely, and compare JWTs with sessions.",
    readTime: 14,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Authentication is where security mistakes hurt most: plain-text passwords, tokens stolen through XSS, or tokens that never expire. Almost every full-stack interview asks how you would implement login, and the details show whether you really understand security." },

      { type: "h2", text: "The mental model: a festival wristband" },
      { type: "p", text: "At a festival gate, you show your ID once (log in with a password). Staff give you a **wristband** (a token) that is hard to forge, because it carries the organiser’s stamp (a signature). At every stage you just show the wristband, and nobody calls the gate again. The wristband expires at midnight (`exp`). That is a JWT." },
      { type: "table", headers: ["Term", "Question it answers"], rows: [
        ["**Authentication (AuthN)**", "Who are you? (login)"],
        ["**Authorisation (AuthZ)**", "What are you allowed to do? (roles, permissions)"]
      ] },

      { type: "h2", text: "Step 1: store passwords safely" },
      { type: "p", text: "Never store passwords in plain text or with fast hashes like MD5 or SHA-256. Use a slow, salted password-hashing algorithm such as **bcrypt** or **argon2**, so that even a leaked database is very expensive to crack:" },
      { type: "code", language: "javascript", code: `import bcrypt from "bcrypt";

// Sign up
const passwordHash = await bcrypt.hash(password, 12);   // 12 = cost factor
await User.create({ email, passwordHash });

// Log in
const user = await User.findOne({ email });
const valid = user && (await bcrypt.compare(password, user.passwordHash));
if (!valid) {
  // same message for "no user" and "wrong password", so emails can't be enumerated
  throw new AppError(401, "INVALID_CREDENTIALS", "Invalid email or password");
}` },
      { type: "callout", variant: "note", text: "A **salt** is random data mixed into each password before hashing, so two users with the same password get different hashes and precomputed “rainbow tables” don’t work. bcrypt generates and stores the salt inside the hash automatically." },

      { type: "h2", text: "Step 2: what a JWT is" },
      { type: "code", language: "text", code: `eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiI2NjEiLCJyb2xlIjoic3R1ZGVudCIsImV4cCI6MTc5MH0.Qm9n...
└────── header ──────┘ └──────────────── payload ────────────────────────┘ └ signature ┘

header:    { "alg": "HS256", "typ": "JWT" }
payload:   { "sub": "661", "role": "student", "iat": 1789990000, "exp": 1789990900 }
signature: HMAC-SHA256(base64(header) + "." + base64(payload), SECRET)` },
      { type: "callout", variant: "warning", text: "A JWT is **signed, not encrypted**. Anyone can base64-decode the payload and read it. Never put passwords, personal data or secrets in it. The signature only proves it wasn’t tampered with." },

      { type: "h2", text: "Step 3: issue and verify tokens" },
      { type: "code", language: "javascript", code: `import jwt from "jsonwebtoken";

const ACCESS_TTL = "15m";

function signAccessToken(user) {
  return jwt.sign(
    { sub: user.id, role: user.role },          // minimal claims
    process.env.JWT_SECRET,                      // long random secret from env
    { expiresIn: ACCESS_TTL }
  );
}

// POST /api/auth/login
app.post("/api/auth/login", validate(loginSchema), asyncHandler(async (req, res) => {
  const user = await verifyCredentials(req.body.email, req.body.password);
  const accessToken = signAccessToken(user);
  res.json({ accessToken, user: { id: user.id, name: user.name, role: user.role } });
}));

// Authentication middleware
function authenticate(req, res, next) {
  const header = req.get("authorization") || "";
  const token = header.startsWith("Bearer ") ? header.slice(7) : null;
  if (!token) return res.status(401).json({ error: { code: "UNAUTHENTICATED" } });

  try {
    const payload = jwt.verify(token, process.env.JWT_SECRET);   // checks signature + exp
    req.user = { id: payload.sub, role: payload.role };
    next();
  } catch {
    res.status(401).json({ error: { code: "INVALID_TOKEN" } });
  }
}` },

      { type: "h2", text: "Step 4: where to store tokens in the browser" },
      { type: "table", headers: ["Storage", "XSS can steal it?", "CSRF risk?", "Notes"], rows: [
        ["`localStorage`", "Yes, any injected script can read it", "No", "Simple, but one XSS bug leaks every token"],
        ["Memory (a JS variable)", "Harder", "No", "Lost on refresh, so pair it with a refresh cookie"],
        ["`httpOnly` cookie", "No, JavaScript can’t read it", "Yes, mitigate with `SameSite` and CSRF tokens", "The recommended choice for web apps"]
      ] },
      { type: "code", language: "javascript", code: `res.cookie("refresh_token", refreshToken, {
  httpOnly: true,                 // not readable by JavaScript
  secure: true,                   // HTTPS only
  sameSite: "strict",             // not sent on cross-site requests
  path: "/api/auth/refresh",      // only sent to the refresh endpoint
  maxAge: 7 * 24 * 60 * 60 * 1000 // 7 days
});` },

      { type: "h2", text: "Step 5: access and refresh tokens" },
      { type: "p", text: "Short-lived access tokens (about 15 minutes) limit the damage if one is stolen. A long-lived **refresh token** in an httpOnly cookie gets new access tokens without asking the user to log in again:" },
      { type: "ol", items: [
        "Login returns an access token (kept in memory) and sets a refresh-token cookie.",
        "The frontend sends `Authorization: Bearer <access>` with API calls.",
        "When an API call returns 401 because the token expired, the frontend calls `POST /api/auth/refresh`. The cookie is sent automatically.",
        "The server verifies the refresh token against the database, **rotates** it (issues a new one and invalidates the old one) and returns a new access token.",
        "Logout deletes the refresh token on the server and clears the cookie."
      ] },
      { type: "callout", variant: "tip", text: "Storing refresh tokens (or their hashes) in the database lets you revoke sessions with “log out of all devices” and detect reuse of stolen tokens. Pure stateless JWTs can’t be revoked before they expire." },

      { type: "h2", text: "JWT vs server sessions" },
      { type: "table", headers: ["", "Server sessions", "JWT"], rows: [
        ["Where state lives", "On the server (Redis or a database), and the cookie holds only an ID", "In the token, so the server needs no lookup"],
        ["Revocation", "Easy: delete the session", "Hard until expiry, unless you add a denylist or refresh tokens"],
        ["Scaling", "Needs a shared session store", "Stateless, so it works across many services"],
        ["Best for", "Classic web apps on one domain", "APIs, mobile apps, microservices"]
      ] },

      { type: "h2", text: "Security checklist" },
      { type: "ul", items: [
        "Use HTTPS everywhere.",
        "Hash passwords with bcrypt or argon2, and never log passwords or tokens.",
        "Rate-limit login, sign-up and OTP endpoints.",
        "Use long, random secrets from environment variables, and rotate them if they leak.",
        "Use short access-token lifetimes, and rotate refresh tokens on each use.",
        "Return the same error for an unknown email and a wrong password.",
        "Consider battle-tested providers (Auth0, Clerk, Firebase Auth) or libraries (Auth.js, Passport) before building everything yourself."
      ] },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "How would you store user passwords?", answer: ["Never in plain text or with fast hashes. Hash them with a slow, salted algorithm such as bcrypt (cost about 12) or argon2id, store only the hash, and compare with the library’s constant-time compare function on login."] },
      { type: "qa", question: "What are the three parts of a JWT?", answer: ["A header (algorithm and type), a payload (claims such as `sub`, `role`, `iat` and `exp`) and a signature. Each part is base64url-encoded and joined with dots. The signature is computed over the header and payload with a secret or private key, so tampering is detected."] },
      { type: "qa", question: "Is data inside a JWT secure?", answer: ["It is tamper-proof but not confidential, because the payload is only base64-encoded and anyone can read it. Put only non-sensitive identifiers and claims in it, and use JWE if you truly need encryption."] },
      { type: "qa", question: "Where should a web app store tokens?", answer: ["Preferably in `httpOnly`, `Secure`, `SameSite` cookies, which JavaScript can’t read, so XSS can’t steal them, with CSRF protection. Keep a short-lived access token in memory and the refresh token in an httpOnly cookie. Avoid `localStorage` for sensitive tokens."] },
      { type: "qa", question: "How do you log a user out with JWTs?", answer: ["Delete or revoke the refresh token on the server and clear the cookies. The access token stays valid until it expires, which is why lifetimes are short. For immediate revocation, check a denylist of token IDs (`jti`) or a token-version field on the user."] },
      { type: "qa", question: "What is the difference between authentication and authorisation?", answer: ["Authentication verifies identity: who you are. Authorisation decides permissions: what you can do. In HTTP terms, failing authentication returns 401, and being authenticated but not permitted returns 403."] },

      { type: "exercise", title: "Implement the refresh endpoint", task: "Write `POST /api/auth/refresh`. Read the `refresh_token` cookie, verify it with `JWT_REFRESH_SECRET`, check that its `jti` exists in a `RefreshToken` collection, delete it (rotation), issue a new refresh token and cookie, and return a new access token. Return 401 on any failure.", language: "javascript", solution: `import crypto from "node:crypto";

app.post("/api/auth/refresh", asyncHandler(async (req, res) => {
  const token = req.cookies.refresh_token;
  if (!token) return res.status(401).json({ error: { code: "NO_REFRESH_TOKEN" } });

  let payload;
  try {
    payload = jwt.verify(token, process.env.JWT_REFRESH_SECRET);
  } catch {
    return res.status(401).json({ error: { code: "INVALID_REFRESH_TOKEN" } });
  }

  // Rotation: the old token must exist, and is consumed exactly once
  const stored = await RefreshToken.findOneAndDelete({ jti: payload.jti, userId: payload.sub });
  if (!stored) {
    // reuse of an already-rotated token: possible theft, so revoke everything
    await RefreshToken.deleteMany({ userId: payload.sub });
    return res.status(401).json({ error: { code: "REFRESH_TOKEN_REUSED" } });
  }

  const user = await User.findById(payload.sub);
  const jti = crypto.randomUUID();
  await RefreshToken.create({ jti, userId: user.id });

  const newRefresh = jwt.sign({ sub: user.id, jti }, process.env.JWT_REFRESH_SECRET, { expiresIn: "7d" });
  res.cookie("refresh_token", newRefresh, {
    httpOnly: true, secure: true, sameSite: "strict",
    path: "/api/auth/refresh", maxAge: 7 * 24 * 60 * 60 * 1000
  });

  res.json({ accessToken: signAccessToken(user) });
}));`, explanation: "`findOneAndDelete` makes each refresh token single-use. If a token shows up that was already rotated, someone may be replaying a stolen token, so all of that user’s sessions are revoked. A separate secret for refresh tokens means an access-token secret leak doesn’t compromise refresh tokens." },

      { type: "summary", items: [
        "Hash passwords with bcrypt or argon2, and never store or log plain passwords.",
        "A JWT is a signed, readable wristband. Keep the claims minimal and the lifetime short.",
        "Store refresh tokens in `httpOnly`, `Secure`, `SameSite` cookies, and avoid `localStorage` for secrets.",
        "Rotate refresh tokens and keep them server-side so sessions can be revoked.",
        "401 means not authenticated and 403 means not allowed. Rate-limit every auth endpoint."
      ] }
    ]
  },

  {
    slug: "production-ready-node",
    title: "Production-Ready Node.js",
    summary: "Configuration, logging, security, graceful shutdown, scaling with clusters and containers, and deployment.",
    readTime: 12,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Code that works on your laptop isn’t ready for real users. Production apps must handle secrets safely, survive crashes, log usefully, shut down without dropping requests and use every CPU core. This is the knowledge that separates hobby projects from professional backends." },

      { type: "h2", text: "The mental model: the 12-factor app" },
      { type: "p", text: "Treat your server like **cattle, not a pet**: any instance can be killed and replaced at any time, so there is no special state on its disk and configuration comes from the environment. The “12-factor app” principles describe this. The most important ones for Node are config in the environment, stateless processes, logs as event streams and disposability." },

      { type: "h2", text: "Configuration and secrets" },
      { type: "code", language: "javascript", code: `// config.js: validate env vars once at startup, and fail fast if any are missing
import { z } from "zod";

const env = z.object({
  NODE_ENV: z.enum(["development", "test", "production"]).default("development"),
  PORT: z.coerce.number().default(8000),
  MONGO_URI: z.string().url(),
  JWT_SECRET: z.string().min(32)
}).parse(process.env);

export default env;` },
      { type: "ul", items: [
        "Keep secrets in environment variables or a secrets manager (AWS SSM Parameter Store or Secrets Manager, Vault), never in code.",
        "Commit `.env.example` with placeholder values. Add `.env` to `.gitignore`.",
        "If a secret is ever committed, rotate it. Deleting the commit isn’t enough, because it stays in the git history."
      ] },

      { type: "h2", text: "Structured logging" },
      { type: "compare", badLabel: "console.log everywhere", bad: `console.log("user logged in " + user.email);
console.log(err);`, goodLabel: "Structured JSON logs", good: `import pino from "pino";
const logger = pino({ level: process.env.LOG_LEVEL || "info" });

logger.info({ userId: user.id, requestId }, "user logged in");
logger.error({ err, requestId }, "payment failed");` },
      { type: "p", text: "JSON logs can be searched and filtered in tools like CloudWatch, Datadog or Grafana Loki. Add a **request ID** to every log line so you can follow a single request through the system, and never log passwords, tokens or full card numbers." },

      { type: "h2", text: "Graceful shutdown" },
      { type: "p", text: "During deploys, the platform sends `SIGTERM`. If the process exits immediately, in-flight requests fail. A graceful shutdown stops accepting new connections, finishes current ones and then closes resources:" },
      { type: "code", language: "javascript", code: `const server = app.listen(env.PORT, () => logger.info("listening on " + env.PORT));

async function shutdown(signal) {
  logger.info({ signal }, "shutting down");
  server.close(async () => {           // stop accepting, wait for open requests
    await mongoose.connection.close();
    logger.info("closed cleanly");
    process.exit(0);
  });
  setTimeout(() => process.exit(1), 10_000).unref();   // force exit if stuck
}

process.on("SIGTERM", shutdown);
process.on("SIGINT", shutdown);` },

      { type: "h2", text: "Health checks" },
      { type: "code", language: "javascript", code: `// Liveness: is the process alive?
app.get("/health/live", (req, res) => res.json({ status: "ok" }));

// Readiness: can it serve traffic (database connected)?
app.get("/health/ready", (req, res) => {
  const dbReady = mongoose.connection.readyState === 1;
  res.status(dbReady ? 200 : 503).json({ db: dbReady ? "up" : "down" });
});` },

      { type: "h2", text: "Using all CPU cores" },
      { type: "p", text: "One Node process uses one core for JavaScript. To use all of them, run **several processes**:" },
      { type: "table", headers: ["Approach", "How"], rows: [
        ["Node cluster module", "A primary process forks one worker per core, and they share the port"],
        ["PM2", "`pm2 start server.js -i max` handles clustering, restarts and log management"],
        ["Containers", "Run one process per container and scale the containers (Docker, Kubernetes, ECS)"]
      ] },
      { type: "callout", variant: "note", text: "With several processes, in-memory state (sessions, caches, rate-limit counters) isn’t shared. Move it to Redis or a database, so that stateless processes can be added or removed freely." },

      { type: "h2", text: "A production Dockerfile" },
      { type: "code", language: "dockerfile", code: `FROM node:20-alpine
WORKDIR /app

# Install only production dependencies, exactly from the lockfile
COPY package*.json ./
RUN npm ci --omit=dev

COPY src ./src
ENV NODE_ENV=production

# Don't run as root
USER node
EXPOSE 8000
CMD ["node", "src/server.js"]` },

      { type: "h2", text: "Security hardening checklist" },
      { type: "table", headers: ["Risk", "Mitigation"], rows: [
        ["Injection (NoSQL or SQL)", "Validate inputs with schemas, use parameterised queries, and reject `$` operators in user input"],
        ["XSS through API data", "Escape output on the frontend, and set CSP headers (helmet)"],
        ["Brute force", "Rate-limit auth routes, and lock accounts or add a CAPTCHA after repeated failures"],
        ["Vulnerable dependencies", "Run `npm audit`, use Dependabot or Renovate, and keep dependencies few"],
        ["Leaked secrets", "Use env or a secrets manager, rotate secrets, and add secret scanning in CI"],
        ["Large payload denial of service", "Set body size limits, timeouts and pagination caps"],
        ["Running as root", "Set `USER node` in the container"]
      ] },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "How do you manage configuration and secrets in Node?", answer: ["Read them from environment variables, validated at startup so the app fails fast if anything is missing. Load secrets from a secrets manager in production, never commit them, provide `.env.example` for developers, and rotate anything that leaks."] },
      { type: "qa", question: "What is graceful shutdown and why does it matter?", answer: ["When the process receives SIGTERM, it stops accepting new connections, lets in-flight requests finish, closes database connections and exits, with a timeout as a fallback. It prevents dropped requests and corrupted work during deploys and scaling events."] },
      { type: "qa", question: "How do you scale a Node.js application?", answer: ["Vertically within a machine, using cluster or PM2 to run one process per core. Horizontally across machines or containers behind a load balancer. Keep the processes stateless with shared session and cache storage in Redis, offload CPU-heavy work to queues and workers, and cache aggressively."] },
      { type: "qa", question: "Why use structured logging?", answer: ["JSON logs with consistent fields such as level, timestamp, request ID and user ID can be searched, filtered and aggregated by log platforms, which makes debugging production issues and building alerts much faster than with free-text `console.log` output."] },
      { type: "qa", question: "What is the difference between liveness and readiness checks?", answer: ["Liveness asks whether the process is alive. If not, the orchestrator restarts it. Readiness asks whether it can serve traffic right now, for example whether the database is connected. If not, the load balancer stops sending requests but doesn’t restart it."] },

      { type: "exercise", title: "Add request IDs to every log line", task: "Write middleware that gives every request a unique ID, taken from the incoming `x-request-id` header or generated with `crypto.randomUUID()`. Attach a child logger with that ID to `req.log`, set the ID on the response header, and log one line per completed request with the method, URL, status and duration.", language: "javascript", solution: `import crypto from "node:crypto";
import pino from "pino";

const logger = pino();

function requestContext(req, res, next) {
  const requestId = req.get("x-request-id") || crypto.randomUUID();
  const start = process.hrtime.bigint();

  req.id = requestId;
  req.log = logger.child({ requestId });    // every log line includes the ID
  res.set("x-request-id", requestId);       // the client can quote it to support

  res.on("finish", () => {
    const durationMs = Number(process.hrtime.bigint() - start) / 1e6;
    req.log.info(
      { method: req.method, url: req.originalUrl, status: res.statusCode, durationMs },
      "request completed"
    );
  });

  next();
}

app.use(requestContext);

// later, in any handler:
// req.log.warn({ courseId }, "course not found");`, explanation: "A child logger automatically adds `requestId` to every line, so all logs from one request can be pulled together in your log tool. Reusing an incoming `x-request-id` lets you trace a request across several services." },

      { type: "summary", items: [
        "Validate config from the environment at startup, and keep secrets out of code.",
        "Use structured JSON logs with request IDs, and never log secrets.",
        "Shut down gracefully on SIGTERM, and expose liveness and readiness checks.",
        "Scale with several stateless processes (cluster, PM2 or containers) and move shared state to Redis.",
        "Harden the app: validation, rate limits, dependency audits and running as a non-root user."
      ] }
    ]
  }
];
